/**
 * Incorporation Services Content
 * Public Limited Company and Business Conversions
 */

export const publicLimitedCompany = {
  id: "public-limited-company",
  slug: "public-limited-company",
  seoTitle: "Public Limited Company Registration in India | Expert Incorporation Services",
  seoDescription: "Register your Public Limited Company with expert guidance. Get access to public capital markets, enhanced credibility, and structured governance. Professional incorporation services.",
  
  heroTitle: "Public Limited Company Registration",
  heroSubtitle: "Corporate Incorporation",
  
  summary: "A Public Limited Company is the ideal business structure for large enterprises planning to raise capital from the public through stock exchanges. It offers unlimited growth potential, access to capital markets, and enhanced credibility among investors, stakeholders, and the public. Our expert team handles the complete incorporation process, ensuring compliance with SEBI regulations and the Companies Act, 2013.",
  
  whoNeeds: {
    title: "Who Should Register a Public Limited Company?",
    description: "A Public Limited Company structure is ideal for businesses planning large-scale operations, public fundraising, or listing on stock exchanges.",
    points: [
      "Businesses planning to raise capital through IPO or public offerings",
      "Large corporations requiring access to capital markets",
      "Companies with operations exceeding Rs. 500 crores",
      "Businesses planning stock exchange listing (BSE/NSE)",
      "Enterprises seeking enhanced public trust and credibility",
      "Private Limited Companies ready to convert and scale",
      "Government contractors and suppliers for large projects",
      "Infrastructure and real estate development companies"
    ]
  },
  
  whyRequired: {
    title: "Why Register as a Public Limited Company?",
    description: "Public Limited Company status offers unparalleled benefits for businesses seeking massive growth and public participation.",
    points: [
      "Access unlimited capital through public share offerings",
      "Get listed on stock exchanges (BSE, NSE) for liquidity",
      "Enhanced credibility with banks, government, and investors",
      "Attract institutional investors and foreign capital",
      "Professional management with board governance structure",
      "Separate legal entity with perpetual succession",
      "Easy transferability of shares through stock markets",
      "Brand recognition and market positioning advantages"
    ]
  },
  
  benefits: [
    {
      title: "Capital Market Access",
      description: "Raise unlimited capital through IPOs, FPOs, rights issues, and other public offerings"
    },
    {
      title: "Stock Exchange Listing",
      description: "Get listed on BSE/NSE for enhanced liquidity and shareholder value"
    },
    {
      title: "Credibility & Trust",
      description: "Highest level of business credibility with stringent compliance requirements"
    },
    {
      title: "Institutional Investment",
      description: "Attract FIIs, mutual funds, pension funds, and other institutional investors"
    },
    {
      title: "Employee Stock Options",
      description: "Offer ESOPs to attract and retain top talent through equity participation"
    },
    {
      title: "Merger & Acquisition Ready",
      description: "Easier M&A transactions with publicly traded shares and clear valuation"
    }
  ],
  
  documentsRequired: [
    {
      category: "Identity & Address Proof",
      items: [
        "PAN Card of all directors (minimum 3 required)",
        "Aadhaar Card of all directors",
        "Passport (mandatory for foreign directors)",
        "Passport-size photographs of directors",
        "Latest address proof (utility bill/bank statement)"
      ]
    },
    {
      category: "Registered Office Proof",
      items: [
        "Registered office address proof",
        "NOC from property owner",
        "Rent agreement (if rented premises)",
        "Latest utility bill (not older than 2 months)",
        "Property documents (if owned)"
      ]
    },
    {
      category: "Company Documents",
      items: [
        "Memorandum of Association (MOA)",
        "Articles of Association (AOA)",
        "Board resolution for incorporation",
        "Declaration by first directors",
        "Digital Signature Certificates for directors"
      ]
    }
  ],
  
  process: [
    {
      step: 1,
      title: "Consultation & Planning",
      description: "Expert consultation to understand your business requirements, capital needs, and compliance obligations. We help structure your company for optimal governance."
    },
    {
      step: 2,
      title: "DSC & DIN Application",
      description: "Apply for Digital Signature Certificates and Director Identification Numbers for all directors. Minimum 3 directors required for public company."
    },
    {
      step: 3,
      title: "Name Reservation",
      description: "Apply for company name reservation through SPICe+ form with RUN (Reserve Unique Name) service. We ensure name availability and compliance."
    },
    {
      step: 4,
      title: "Document Preparation",
      description: "Draft comprehensive MOA and AOA suitable for public company with appropriate capital structure, share classes, and governance provisions."
    },
    {
      step: 5,
      title: "SPICe+ Filing",
      description: "File incorporation application with all supporting documents through MCA SPICe+ portal for company registration."
    },
    {
      step: 6,
      title: "Certificate of Incorporation",
      description: "Receive Certificate of Incorporation with CIN, PAN, TAN, GST, EPF, and ESI registrations through integrated filing."
    },
    {
      step: 7,
      title: "Commence Business",
      description: "Obtain Certificate of Commencement of Business after receiving initial share capital from subscribers."
    }
  ],
  
  timeline: {
    total: "20-30 working days",
    breakdown: [
      { phase: "DSC & DIN", duration: "3-5 days" },
      { phase: "Name Approval", duration: "2-3 days" },
      { phase: "Document Preparation", duration: "5-7 days" },
      { phase: "SPICe+ Filing", duration: "7-10 days" },
      { phase: "Commencement Certificate", duration: "3-5 days" }
    ]
  },
  
  pricing: {
    startingFrom: "₹49,999",
    includes: [
      "Complete incorporation filing",
      "DSC for 3 directors",
      "DIN for 3 directors",
      "MOA & AOA drafting",
      "PAN & TAN registration",
      "Professional fee",
      "Government fee up to Rs. 5 lakh capital"
    ],
    governmentFees: "Additional based on authorized capital"
  },
  
  faqs: [
    {
      question: "What is the minimum capital required for a Public Limited Company?",
      answer: "There is no minimum paid-up capital requirement post-2015 amendments. However, for IPO listing, SEBI requires minimum Rs. 10 crores net tangible assets and other criteria."
    },
    {
      question: "How many directors and shareholders are required?",
      answer: "A Public Limited Company requires minimum 3 directors and 7 shareholders. At least one director must be resident in India (stayed 182+ days in preceding calendar year)."
    },
    {
      question: "What is the difference between Private and Public Limited Company?",
      answer: "Public companies can raise capital from public, list on stock exchanges, have no restriction on share transferability, require more directors (3 vs 2), and have stricter compliance requirements."
    },
    {
      question: "Can a Public Limited Company have foreign directors?",
      answer: "Yes, foreign nationals can be directors. However, at least one director must be an Indian resident. Foreign directors need valid passport, address proof, and may require DIN."
    },
    {
      question: "What compliances are required for a Public Limited Company?",
      answer: "Public companies have extensive compliances including quarterly board meetings, annual general meeting, multiple ROC filings, statutory auditor appointment, and if listed, SEBI compliances."
    },
    {
      question: "Can I convert my Private Limited to Public Limited Company?",
      answer: "Yes, Private Limited Companies can convert to Public Limited by following the prescribed procedure including board and shareholder approval, ROC filing, and compliance with section 18 requirements."
    }
  ],
  
  relatedServices: [
    { name: "Private Limited Company", slug: "private-limited-company" },
    { name: "Private to Public Conversion", slug: "private-to-public-limited" },
    { name: "ROC Filing", slug: "roc-filing" },
    { name: "Annual Filing", slug: "annual-filing" }
  ],
  
  trustSignals: {
    experience: "10+ Years Experience",
    experts: "CS & CA Experts",
    companies: "200+ Public Companies Registered",
    support: "Dedicated Account Manager"
  }
};

export const opcToPrivateLimited = {
  id: "opc-to-private-limited",
  slug: "opc-to-private-limited",
  seoTitle: "OPC to Private Limited Conversion | Company Structure Upgrade",
  seoDescription: "Convert your One Person Company to Private Limited Company. Add partners, expand shareholding, and unlock growth opportunities. Expert conversion services.",
  
  heroTitle: "OPC to Private Limited Conversion",
  heroSubtitle: "Business Conversion",
  
  summary: "Convert your One Person Company (OPC) to a Private Limited Company to add shareholders, expand your business, and unlock better funding opportunities. This conversion is mandatory once your OPC crosses Rs. 50 lakh paid-up capital or Rs. 2 crore turnover, or voluntary if you want to bring in partners. Our experts handle the complete conversion process seamlessly.",
  
  whoNeeds: {
    title: "Who Should Convert OPC to Private Limited?",
    description: "Conversion becomes necessary or beneficial in several business scenarios.",
    points: [
      "OPCs exceeding Rs. 50 lakh paid-up capital threshold",
      "OPCs crossing Rs. 2 crore annual turnover",
      "Single founders wanting to bring in co-founders or investors",
      "Businesses seeking venture capital or angel investment",
      "OPCs planning to scale operations significantly",
      "Founders wanting to implement ESOP schemes",
      "Companies requiring multiple shareholders for partnerships",
      "Businesses voluntarily upgrading for strategic reasons"
    ]
  },
  
  whyRequired: {
    title: "Why Convert from OPC to Private Limited?",
    description: "Conversion opens new opportunities for business growth and partnership.",
    points: [
      "Add unlimited shareholders and distribute ownership",
      "Attract investments from VCs, angels, and institutional investors",
      "Remove single-member limitation for growth",
      "Better scalability and expansion opportunities",
      "Implement employee stock option plans (ESOPs)",
      "Enhanced credibility with larger clients",
      "Comply with mandatory conversion requirements",
      "Succession planning with multiple stakeholders"
    ]
  },
  
  benefits: [
    {
      title: "Multiple Shareholders",
      description: "Add up to 200 shareholders and distribute equity among partners, investors, and employees"
    },
    {
      title: "Investment Ready",
      description: "Private Limited structure is preferred by VCs, angels, and institutional investors"
    },
    {
      title: "ESOP Implementation",
      description: "Implement employee stock option schemes to attract and retain talent"
    },
    {
      title: "Scalability",
      description: "Remove single-member constraints and scale business operations freely"
    },
    {
      title: "Regulatory Compliance",
      description: "Meet mandatory conversion requirements and avoid penalties"
    },
    {
      title: "Business Partnerships",
      description: "Enter joint ventures and partnerships with equity participation"
    }
  ],
  
  documentsRequired: [
    {
      category: "Company Documents",
      items: [
        "Certificate of Incorporation of OPC",
        "Current MOA and AOA",
        "Latest audited financial statements",
        "Board resolution for conversion",
        "Shareholder resolution (special resolution)"
      ]
    },
    {
      category: "New Shareholder Documents",
      items: [
        "PAN Card of new shareholders",
        "Aadhaar Card of new shareholders",
        "Address proof of new shareholders",
        "Passport-size photographs",
        "Consent and declaration from new directors"
      ]
    },
    {
      category: "Supporting Documents",
      items: [
        "Statement of assets and liabilities",
        "List of creditors and debtors",
        "Income Tax returns of the company",
        "Bank statements for last 6 months",
        "CA certificate for financials (if required)"
      ]
    }
  ],
  
  process: [
    {
      step: 1,
      title: "Assessment & Planning",
      description: "Review your OPC financials, understand conversion triggers (mandatory or voluntary), and plan the new shareholding structure."
    },
    {
      step: 2,
      title: "Board & Member Approval",
      description: "Pass board resolution and special resolution by the sole member approving the conversion and new share structure."
    },
    {
      step: 3,
      title: "New Shareholder Onboarding",
      description: "Collect KYC documents from new shareholders and directors. Obtain DSC and DIN for new directors if required."
    },
    {
      step: 4,
      title: "Draft Altered MOA & AOA",
      description: "Prepare amended Memorandum and Articles of Association removing OPC-specific clauses and adding Private Limited provisions."
    },
    {
      step: 5,
      title: "File Form INC-6",
      description: "File conversion application (Form INC-6) with ROC along with all supporting documents and resolutions."
    },
    {
      step: 6,
      title: "Receive New COI",
      description: "Upon approval, receive new Certificate of Incorporation reflecting conversion to Private Limited Company."
    }
  ],
  
  timeline: {
    total: "15-25 working days",
    breakdown: [
      { phase: "Planning & Documentation", duration: "3-5 days" },
      { phase: "Board & Member Resolution", duration: "2-3 days" },
      { phase: "MOA/AOA Alteration", duration: "3-5 days" },
      { phase: "ROC Filing & Processing", duration: "7-12 days" }
    ]
  },
  
  pricing: {
    startingFrom: "₹14,999",
    includes: [
      "Complete conversion filing",
      "MOA & AOA alteration",
      "All ROC forms and fees",
      "New Certificate of Incorporation",
      "Professional consultation",
      "Post-conversion compliance support"
    ],
    governmentFees: "Included in package"
  },
  
  faqs: [
    {
      question: "Is OPC to Private Limited conversion mandatory?",
      answer: "Yes, conversion is mandatory when paid-up capital exceeds Rs. 50 lakh OR average turnover of immediately preceding 3 consecutive years exceeds Rs. 2 crore. Voluntary conversion is also allowed anytime."
    },
    {
      question: "How many shareholders are required after conversion?",
      answer: "After conversion to Private Limited, you need minimum 2 shareholders. The existing OPC member continues as one shareholder, and at least one new shareholder must be added."
    },
    {
      question: "What happens to the nominee in OPC after conversion?",
      answer: "The nominee requirement is only for OPC. After conversion to Private Limited, the nominee relationship ceases and the nominee can either become a shareholder or director if desired."
    },
    {
      question: "Will the company's CIN change after conversion?",
      answer: "Yes, the CIN (Corporate Identification Number) will change to reflect the new company type. A new Certificate of Incorporation with updated CIN will be issued."
    },
    {
      question: "What is the timeline for mandatory conversion?",
      answer: "For mandatory conversion, you must convert within 6 months from the date of exceeding the threshold (capital or turnover). Non-compliance attracts penalties."
    },
    {
      question: "Can the existing OPC member remain as sole director?",
      answer: "Private Limited Company requires minimum 2 directors. So, either the existing sole director continues with one additional director, or new directors can be appointed."
    }
  ],
  
  relatedServices: [
    { name: "One Person Company", slug: "one-person-company" },
    { name: "Private Limited Company", slug: "private-limited-company" },
    { name: "Annual Filing", slug: "annual-filing" },
    { name: "ROC Filing", slug: "roc-filing" }
  ],
  
  trustSignals: {
    experience: "8+ Years Experience",
    experts: "CS & Legal Experts",
    conversions: "500+ Conversions Completed",
    support: "End-to-End Support"
  }
};

export const llpToPrivateLimited = {
  id: "llp-to-private-limited",
  slug: "llp-to-private-limited",
  seoTitle: "LLP to Private Limited Conversion | Business Structure Upgrade",
  seoDescription: "Convert your LLP to Private Limited Company for equity funding, ESOPs, and better growth opportunities. Expert conversion services with seamless transition.",
  
  heroTitle: "LLP to Private Limited Conversion",
  heroSubtitle: "Business Conversion",
  
  summary: "Convert your Limited Liability Partnership (LLP) to a Private Limited Company to unlock equity funding opportunities, implement employee stock options, and prepare for strategic growth. This conversion is ideal when your LLP needs external investment or you want to leverage the corporate structure benefits. Our experts ensure a smooth transition with minimal business disruption.",
  
  whoNeeds: {
    title: "Who Should Convert LLP to Private Limited?",
    description: "Conversion is beneficial for LLPs looking to scale through external investment or corporate restructuring.",
    points: [
      "LLPs seeking venture capital or angel investment",
      "Businesses wanting to implement ESOP schemes",
      "LLPs planning for eventual public listing",
      "Partners wanting to restructure ownership as shares",
      "Businesses requiring corporate governance structure",
      "LLPs attracting strategic investors or acquirers",
      "Growing businesses needing enhanced credibility",
      "LLPs planning international expansion"
    ]
  },
  
  whyRequired: {
    title: "Why Convert from LLP to Private Limited?",
    description: "Private Limited structure offers advantages that LLP cannot provide for growth-focused businesses.",
    points: [
      "Equity funding through shares (not possible in LLP)",
      "Implement employee stock option plans (ESOPs)",
      "Better structured corporate governance",
      "Preferred structure for VC and PE investments",
      "Easier M&A transactions with share transfers",
      "Path to eventual IPO and public listing",
      "Enhanced credibility with larger enterprises",
      "Clear ownership structure with share capital"
    ]
  },
  
  benefits: [
    {
      title: "Equity Funding Access",
      description: "Issue shares to investors, unlike LLP which can only offer profit-sharing arrangements"
    },
    {
      title: "ESOP Ready",
      description: "Implement comprehensive employee stock option schemes to attract and retain talent"
    },
    {
      title: "Investment Attractiveness",
      description: "VCs and PEs strongly prefer Private Limited structure for investment"
    },
    {
      title: "M&A Readiness",
      description: "Share-based structure simplifies mergers, acquisitions, and exit transactions"
    },
    {
      title: "Clear Ownership",
      description: "Shareholding pattern provides clear and documented ownership structure"
    },
    {
      title: "Future IPO Path",
      description: "Creates pathway for eventual public listing if business scales significantly"
    }
  ],
  
  documentsRequired: [
    {
      category: "LLP Documents",
      items: [
        "LLP Agreement (original and all supplements)",
        "Certificate of Incorporation of LLP",
        "Latest audited financial statements",
        "Income Tax returns for last 3 years",
        "All ROC filings and compliance records"
      ]
    },
    {
      category: "Partners Documentation",
      items: [
        "PAN Card of all partners",
        "Aadhaar Card and address proof",
        "Partners' consent for conversion",
        "DPIN/DIN of partners",
        "Digital Signature Certificates"
      ]
    },
    {
      category: "Conversion Documents",
      items: [
        "Statement of assets and liabilities",
        "List of all creditors with amounts",
        "Valuation report (if required)",
        "NOC from creditors (if applicable)",
        "Board resolution of approving partners"
      ]
    }
  ],
  
  process: [
    {
      step: 1,
      title: "Pre-Conversion Assessment",
      description: "Review LLP finances, partnership structure, and compliance status. Plan the new shareholding pattern among partners."
    },
    {
      step: 2,
      title: "Partner Approval",
      description: "Obtain unanimous consent from all partners for the conversion as required under the LLP Act."
    },
    {
      step: 3,
      title: "Creditor Settlement",
      description: "Notify all creditors of the intended conversion. Obtain NOCs where required and settle any objections."
    },
    {
      step: 4,
      title: "Document Preparation",
      description: "Draft new MOA and AOA for the Private Limited Company. Prepare valuation and asset transfer documents."
    },
    {
      step: 5,
      title: "Application Filing",
      description: "File conversion application with ROC along with all prescribed forms, resolutions, and supporting documents."
    },
    {
      step: 6,
      title: "ROC Approval",
      description: "ROC reviews the application, verifies compliance, and issues new Certificate of Incorporation for Private Limited."
    },
    {
      step: 7,
      title: "Post-Conversion Compliance",
      description: "Update all registrations (GST, bank accounts, contracts) and fulfill any transitional compliance requirements."
    }
  ],
  
  timeline: {
    total: "30-45 working days",
    breakdown: [
      { phase: "Assessment & Planning", duration: "5-7 days" },
      { phase: "Partner & Creditor Approval", duration: "7-10 days" },
      { phase: "Document Preparation", duration: "5-7 days" },
      { phase: "ROC Filing & Processing", duration: "15-20 days" }
    ]
  },
  
  pricing: {
    startingFrom: "₹24,999",
    includes: [
      "Complete conversion filing",
      "MOA & AOA preparation",
      "All ROC forms and fees",
      "New Certificate of Incorporation",
      "Creditor notification process",
      "Post-conversion support"
    ],
    governmentFees: "Included in package"
  },
  
  faqs: [
    {
      question: "Is LLP to Private Limited conversion possible directly?",
      answer: "Yes, Section 366 of Companies Act, 2013 allows LLPs to convert directly to Private Limited Companies following the prescribed procedure and obtaining necessary approvals."
    },
    {
      question: "What happens to LLP partners after conversion?",
      answer: "Partners become shareholders and/or directors of the Private Limited Company. The profit-sharing ratio typically converts to shareholding percentage, though this can be restructured."
    },
    {
      question: "Do we need creditor approval for conversion?",
      answer: "You must provide notice to all creditors. If any creditor raises objections, those must be resolved. Secured creditors may need to provide explicit consent."
    },
    {
      question: "Will existing contracts remain valid after conversion?",
      answer: "Yes, the Private Limited Company succeeds to all assets, liabilities, contracts, and obligations of the LLP. However, counter-parties should be notified of the change."
    },
    {
      question: "What is the tax implication of LLP to Pvt Ltd conversion?",
      answer: "The conversion is generally tax-neutral under Section 47 of Income Tax Act if certain conditions are met, including continuing business and shareholders holding prescribed percentage."
    },
    {
      question: "Can we retain the same name after conversion?",
      answer: "Yes, you can retain the business name but it will change from 'XYZ LLP' to 'XYZ Private Limited'. Name availability check is done during the process."
    }
  ],
  
  relatedServices: [
    { name: "LLP Registration", slug: "llp-registration" },
    { name: "Private Limited Company", slug: "private-limited-company" },
    { name: "Annual Filing", slug: "annual-filing" },
    { name: "Share Transfer", slug: "share-transfer" }
  ],
  
  trustSignals: {
    experience: "10+ Years Experience",
    experts: "CS & CA Professionals",
    conversions: "300+ LLP Conversions",
    support: "Seamless Transition"
  }
};

export const privateToPublicLimited = {
  id: "private-to-public-limited",
  slug: "private-to-public-limited",
  seoTitle: "Private Limited to Public Limited Conversion | IPO Ready Structure",
  seoDescription: "Convert your Private Limited to Public Limited Company for IPO readiness, access to capital markets, and enhanced credibility. Expert conversion services.",
  
  heroTitle: "Private to Public Limited Conversion",
  heroSubtitle: "Corporate Conversion",
  
  summary: "Convert your Private Limited Company to a Public Limited Company to access capital markets, prepare for IPO, and unlock growth opportunities. This conversion is essential for businesses planning to go public, raise capital from the general public, or seeking enhanced corporate governance. Our expert team handles the complex conversion process ensuring SEBI compliance and smooth transition.",
  
  whoNeeds: {
    title: "Who Should Convert to Public Limited?",
    description: "Conversion to Public Limited is ideal for companies ready for significant growth and public market access.",
    points: [
      "Companies planning Initial Public Offering (IPO)",
      "Businesses seeking capital from public investors",
      "Companies wanting stock exchange listing",
      "Large enterprises requiring enhanced governance",
      "Businesses seeking institutional investor access",
      "Companies meeting SEBI listing criteria",
      "Firms wanting wider public participation",
      "Strategic expansion through public capital"
    ]
  },
  
  whyRequired: {
    title: "Why Convert to Public Limited Company?",
    description: "Public Limited structure offers unique advantages for large-scale growth and market access.",
    points: [
      "Raise capital through public offerings (IPO, FPO)",
      "List shares on stock exchanges (BSE, NSE)",
      "Unlimited number of shareholders",
      "Enhanced market credibility and trust",
      "Access to institutional investors",
      "Improved liquidity for existing shareholders",
      "Professional corporate governance",
      "Better brand visibility and recognition"
    ]
  },
  
  benefits: [
    {
      title: "Public Capital Access",
      description: "Raise capital from the public through IPOs and other public offerings"
    },
    {
      title: "Stock Exchange Listing",
      description: "List your shares on BSE/NSE for trading and liquidity"
    },
    {
      title: "Unlimited Shareholders",
      description: "Remove 200 shareholder limit of Private Limited structure"
    },
    {
      title: "Institutional Investment",
      description: "Attract mutual funds, FIIs, and other institutional investors"
    },
    {
      title: "Exit Opportunity",
      description: "Provide liquidity and exit options for existing investors"
    },
    {
      title: "Brand Enhancement",
      description: "Enhanced brand recognition as a publicly listed company"
    }
  ],
  
  documentsRequired: [
    {
      category: "Company Documents",
      items: [
        "Certificate of Incorporation",
        "Current MOA and AOA",
        "Audited financial statements (3 years)",
        "Board resolutions for conversion",
        "Shareholder special resolution"
      ]
    },
    {
      category: "Director Documents",
      items: [
        "PAN and identity proof of all directors",
        "DIN documents of existing directors",
        "Details of third director (minimum 3 required)",
        "Director consent forms",
        "Digital Signature Certificates"
      ]
    },
    {
      category: "Additional Requirements",
      items: [
        "Statement of company's affairs",
        "Valuation report (if applicable)",
        "Compliance certificate from CS",
        "NOC from lending institutions",
        "Details of existing shareholders"
      ]
    }
  ],
  
  process: [
    {
      step: 1,
      title: "Eligibility Assessment",
      description: "Review your company's readiness for conversion including financial health, compliance status, and shareholder structure."
    },
    {
      step: 2,
      title: "Board Resolution",
      description: "Convene board meeting to approve the conversion proposal and authorize filing of necessary applications."
    },
    {
      step: 3,
      title: "Special Resolution",
      description: "Pass special resolution in general meeting with 75% shareholder approval for the conversion."
    },
    {
      step: 4,
      title: "Appoint Additional Director",
      description: "Appoint at least one additional director to meet minimum requirement of 3 directors for public company."
    },
    {
      step: 5,
      title: "Alter MOA & AOA",
      description: "Amend MOA and AOA to remove private company restrictions and include public company provisions."
    },
    {
      step: 6,
      title: "File with ROC",
      description: "File Form MGT-14, INC-27, and other required forms with ROC for conversion approval."
    },
    {
      step: 7,
      title: "Obtain New COI",
      description: "Receive new Certificate of Incorporation reflecting status as Public Limited Company."
    }
  ],
  
  timeline: {
    total: "25-40 working days",
    breakdown: [
      { phase: "Assessment & Planning", duration: "5-7 days" },
      { phase: "Board & Shareholder Meetings", duration: "5-7 days" },
      { phase: "MOA/AOA Alteration", duration: "3-5 days" },
      { phase: "ROC Filing & Processing", duration: "12-20 days" }
    ]
  },
  
  pricing: {
    startingFrom: "₹29,999",
    includes: [
      "Complete conversion filing",
      "MOA & AOA alteration",
      "All ROC forms and fees",
      "New Certificate of Incorporation",
      "Compliance certificate",
      "Post-conversion guidance"
    ],
    governmentFees: "Additional based on capital"
  },
  
  faqs: [
    {
      question: "What are the requirements for Pvt to Public conversion?",
      answer: "Key requirements include minimum 3 directors (vs 2 for Pvt Ltd), minimum 7 shareholders, special resolution approval (75%), and altered MOA/AOA removing private company restrictions."
    },
    {
      question: "Does conversion mean automatic stock exchange listing?",
      answer: "No, conversion to Public Limited is separate from stock exchange listing. After conversion, you can apply for listing on BSE/NSE by meeting SEBI requirements through IPO or other routes."
    },
    {
      question: "What happens to existing shareholders?",
      answer: "Existing shareholders continue with their shareholding. Their shares become freely transferable without restriction after conversion (subject to any lock-in agreements)."
    },
    {
      question: "Are there minimum capital requirements?",
      answer: "There's no minimum capital requirement for conversion. However, for stock exchange listing, SEBI has specific criteria including minimum net tangible assets and profitability requirements."
    },
    {
      question: "What additional compliances apply to Public Limited?",
      answer: "Public Limited companies have enhanced compliances including quarterly board meetings, statutory committees, more stringent disclosure requirements, and if listed, SEBI compliance."
    },
    {
      question: "Can we convert back to Private Limited later?",
      answer: "Yes, conversion back is possible but involves complex procedures including application to NCLT and meeting specific criteria. It's advisable to be certain before converting to Public Limited."
    }
  ],
  
  relatedServices: [
    { name: "Private Limited Company", slug: "private-limited-company" },
    { name: "Public Limited Company", slug: "public-limited-company" },
    { name: "Annual Filing", slug: "annual-filing" },
    { name: "ROC Filing", slug: "roc-filing" }
  ],
  
  trustSignals: {
    experience: "12+ Years Experience",
    experts: "CS & SEBI Experts",
    conversions: "100+ Pvt to Public Conversions",
    support: "IPO Readiness Support"
  }
};
